import { _callBackend } from '@/_api/api';
import { fileUpload } from '@/_api/client-upload';
import { downloadDocument as _downloadDocument } from '@/_api/client-documents';
import { saveAs, getDocumentName } from '@/_utils/files';
import type { APIRequest, APIResponsePromise } from '@/_types/api';
import type { SignatureRequest } from '@/_types/models/signatureRequests';

export const getAllSignatureRequests = async ({
  token,
}: APIRequest): APIResponsePromise<SignatureRequest[]> =>
  await _callBackend(
    '/signature_requests/get_all_signature_requests',
    'GET',
    null,
    token,
  );

export const uploadSignatureRequest = async ({
  companyId,
  documentName,
  file,
  path,
  taxYear,
  questionnaireId,
  sectionId,
  token,
}: APIRequest<{
  companyId: number;
  documentName?: string;
  file: File;
  path: string;
  taxYear: number;
  questionnaireId?: number;
  sectionId?: number;
}>): APIResponsePromise<void> => {
  // Get a temporary upload link
  const { upload_link, id } = await _callBackend(
    `/integrations/get_temporary_upload_link?company_id=${companyId}`,
    'GET',
    null,
    token,
  );

  // Upload file
  const url = new URL(upload_link);
  const isDropbox: boolean = url.hostname.includes('dropbox');

  await fileUpload(upload_link, isDropbox, file);

  // Process uploaded document
  await _callBackend(
    '/integrations/process_document',
    'POST',
    {
      id,
      document_name: documentName,
      company_id: companyId,
      path,
      document_type: 'signature_request',
      tax_year: taxYear,
      questionnaire_id: questionnaireId,
      section_id: sectionId,
    },
    token,
  );
};

export const updateSignatureRequest = async ({
  id,
  companyId,
  signatures,
  pages,
  formName,
  status,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
  signatures?: any[];
  pages?: any[];
  formName?: string;
  status?: string;
}>): APIResponsePromise => {
  return await _callBackend(
    '/signature_requests/update_signature_request',
    'POST',
    {
      id,
      company_id: companyId,

      // Remove undefined from data
      ...Object.fromEntries(
        Object.entries({
          signatures,
          pages,
          form_name: formName,
          status,
        }).filter(([_, value]) => value !== undefined),
      ),
    },
    token,
  );
};

export const deleteSignatureRequest = async ({
  id,
  companyId,
  token,
}: APIRequest<{
  id: number;
  companyId: number;
}>): APIResponsePromise => {
  return await _callBackend(
    '/signature_requests/delete_signature_request',
    'POST',
    {
      id,
      company_id: companyId,
    },
    token,
    { returnResponseAsIs: true },
  );
};

export const sendSignatureRequest = async ({
  companyId,
  id,
  documentLocation,
  signatures,
  pages,
  subject,
  message,
  includeWatermark,
  taxYear,
  saveSubjectForFutureUse,
  saveMessageForFutureUse,
  token,
}: APIRequest<{
  companyId: number;
  id: number;
  documentLocation: string;
  signatures: any[];
  pages: any[];
  subject: string;
  message: string;
  includeWatermark: boolean;
  taxYear: number;
  saveSubjectForFutureUse: boolean;
  saveMessageForFutureUse: boolean;
}>): APIResponsePromise => {
  return await _callBackend(
    '/signature_requests/send_signature_request',
    'POST',
    {
      company_id: companyId,
      id,
      document_location: documentLocation,
      signatures,
      pages,
      subject,
      message,
      include_watermark: includeWatermark,
      tax_year: taxYear,
      save_subject_for_future_use: saveSubjectForFutureUse,
      save_message_for_future_use: saveMessageForFutureUse,
    },
    token,
  );
};

export const downloadDocument = async ({
  companyId,
  link,
  token,
}: APIRequest<{
  companyId: number;
  link: string;
}>) => {
  const response = await _downloadDocument({
    companyId,
    link,
    isDownload: true,
    token,
  });

  const downloadLink = response.document.data.link;
  const documentName = getDocumentName(link);

  saveAs(downloadLink, documentName);
};
